import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Guitar, GraduationCap, ArrowRight, Check } from "lucide-react";
import { useStudentAuth } from "../hooks/useStudentAuth";
import { useBordonaAccount } from "../hooks/useBordonaAccount";
import { useInstrument } from "../contexts/InstrumentContext";
// @ts-ignore — auth.js es JS
import authService from "@/firebase/auth";

function splitDisplayName(displayName: string): { firstName: string; lastName: string } {
  const parts = displayName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: "", lastName: "" };
  if (parts.length === 1) return { firstName: parts[0], lastName: "Estudiante" };
  return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
}

export const RoleSelectorPage: React.FC = () => {
  const { role, isLoading: roleLoading, isAuthenticated } = useBordonaAccount();
  const { needsInstrumentPick } = useInstrument();
  const {
    isLoading: authLoading,
    profile,
    user,
    accessProfile,
    error: authError,
    saveProfile,
  } = useStudentAuth();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [rut, setRut] = useState("");
  const [error, setError] = useState("");
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isBootstrapping, setIsBootstrapping] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const handleEmailLogin = async () => {
    setIsLoggingIn(true);
    setLoginError("");
    try {
      await authService.login(loginEmail, loginPassword);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Error al iniciar sesión";
      setLoginError(message);
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleGoogleLogin = async () => {
    setIsLoggingIn(true);
    setLoginError("");
    try {
      await authService.loginWithGoogle();
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Error al iniciar sesión con Google";
      setLoginError(message);
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      setError("Nombre y apellido son obligatorios");
      return;
    }
    setIsSavingProfile(true);
    const ok = await saveProfile(firstName, lastName, rut || undefined);
    setIsSavingProfile(false);
    if (!ok) setError("No se pudo guardar el perfil");
  };

  useEffect(() => {
    if (!isAuthenticated || role !== "student" || profile || isBootstrapping) return;

    const displayName =
      accessProfile?.displayName || user?.displayName || user?.email?.split("@")[0] || "";
    const { firstName: fn, lastName: ln } = splitDisplayName(displayName);
    if (!fn) return;

    let cancelled = false;
    setIsBootstrapping(true);
    saveProfile(fn, ln).finally(() => {
      if (!cancelled) setIsBootstrapping(false);
    });

    return () => {
      cancelled = true;
    };
  }, [
    isAuthenticated,
    role,
    profile,
    accessProfile?.displayName,
    user?.displayName,
    user?.email,
    saveProfile,
  ]);

  const isLoading = authLoading || roleLoading || isBootstrapping;

  if (isLoading) {
    return (
      <div className="h-screen w-screen bg-[#F3EDE3] flex items-center justify-center">
        <div className="h-8 w-8 rounded-full border-2 border-[#7B2335]/20 border-t-[#7B2335] animate-spin" />
      </div>
    );
  }

  if (isAuthenticated && role) {
    return <Navigate to={needsInstrumentPick ? "/instrument" : "/home"} replace />;
  }

  const needsProfileForm =
    isAuthenticated && role === "student" && !profile && !isBootstrapping;

  return (
    <div className="min-h-screen w-screen bg-[#F3EDE3] flex flex-col items-center justify-center relative overflow-hidden py-12">
      <DecorativeGrid />

      <div className="relative z-10 w-full max-w-xl px-6">
        <div className="text-center mb-10" style={{ animation: "fadeUp 0.6s ease-out" }}>
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-[#7B2335] shadow-xl mb-6">
            <Guitar className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-[#2C2825] mb-3 tracking-tight">
            <span className="font-sans">Edusico</span>
            <span className="font-serif italic"> Bordona</span>
          </h1>
          <p className="text-base md:text-lg text-[#8A8078] font-sans max-w-md mx-auto leading-relaxed">
            {needsProfileForm
              ? "Completa tu nombre para continuar al panel."
              : "Inicia sesión con tu cuenta Edusico o entra desde el portal."}
          </p>
        </div>

        {needsProfileForm ? (
          <div
            className="bg-white rounded-2xl border border-[#D4CBC0] p-6 md:p-8 shadow-sm"
            style={{ animation: "fadeUp 0.5s ease-out" }}
          >
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#5A7A3A]/10 mb-3">
                <GraduationCap className="w-6 h-6 text-[#5A7A3A]" />
              </div>
              <h2 className="text-xl font-bold text-[#2C2825]">Tu perfil</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-[#5A524A] block mb-1.5">
                  Nombre *
                </label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border border-[#D4CBC0] bg-[#FAF8F5] text-sm"
                  placeholder="Ej: Juan"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#5A524A] block mb-1.5">
                  Apellido *
                </label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border border-[#D4CBC0] bg-[#FAF8F5] text-sm"
                  placeholder="Ej: Pérez"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#5A524A] block mb-1.5">
                  RUT <span className="text-[#B8AEA3] font-normal">(opcional)</span>
                </label>
                <input
                  type="text"
                  value={rut}
                  onChange={(e) => setRut(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border border-[#D4CBC0] bg-[#FAF8F5] text-sm"
                  placeholder="12.345.678-9"
                />
              </div>
            </div>

            {error && (
              <div className="mt-4 text-sm text-rose-700 bg-rose-50 rounded-xl p-3 border border-rose-100">
                {error}
              </div>
            )}

            <button
              type="button"
              onClick={handleSaveProfile}
              disabled={isSavingProfile}
              className="mt-6 w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#7B2335] text-white font-sans font-medium text-sm disabled:opacity-50"
            >
              {isSavingProfile ? (
                <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <Check className="w-4 h-4" />
              )}
              {isSavingProfile ? "Guardando..." : "Ir a mi panel"}
            </button>
          </div>
        ) : (
          <div
            className="bg-white rounded-2xl border border-[#D4CBC0] p-6 md:p-8 shadow-sm"
            style={{ animation: "fadeUp 0.8s ease-out 0.1s both" }}
          >
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-[#2C2825] mb-1">Inicia sesión</h2>
              <p className="text-sm text-[#8A8078]">
                Lo más fácil: entra desde{" "}
                <a href="https://edusico.cl/portal" className="text-[#7B2335] font-semibold hover:underline">
                  edusico.cl/portal
                </a>{" "}
                y pulsa <strong>Abrir Bordona</strong>.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-[#5A524A] block mb-1.5">Email</label>
                <input
                  type="email"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border border-[#D4CBC0] bg-[#FAF8F5] text-sm"
                  placeholder="tu@email.com"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#5A524A] block mb-1.5">Contraseña</label>
                <input
                  type="password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleEmailLogin()}
                  className="w-full px-4 py-2.5 rounded-xl border border-[#D4CBC0] bg-[#FAF8F5] text-sm"
                  placeholder="••••••••"
                />
              </div>

              <button
                type="button"
                onClick={handleEmailLogin}
                disabled={isLoggingIn}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#7B2335] text-white font-sans font-medium text-sm disabled:opacity-50"
              >
                {isLoggingIn ? (
                  <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <ArrowRight className="w-4 h-4" />
                )}
                {isLoggingIn ? "Iniciando sesión..." : "Iniciar sesión"}
              </button>

              <button
                type="button"
                onClick={handleGoogleLogin}
                disabled={isLoggingIn}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-white border border-[#D4CBC0] text-[#5A524A] font-sans font-medium text-sm disabled:opacity-50"
              >
                Continuar con Google
              </button>
            </div>

            {loginError && (
              <div className="mt-4 text-sm text-rose-700 bg-rose-50 rounded-xl p-3 border border-rose-100 text-center">
                {loginError}
              </div>
            )}

            <div className="mt-6 text-center">
              <p className="text-xs text-[#8A8078]">
                ¿No tienes cuenta?{" "}
                <a
                  href="https://edusico.cl/register"
                  className="text-[#7B2335] font-semibold hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Regístrate en Edusico
                </a>
              </p>
            </div>
          </div>
        )}

        {authError && (
          <div className="mt-4 text-sm text-rose-700 bg-rose-50 rounded-xl p-3 border border-rose-100 text-center">
            {authError}
          </div>
        )}
      </div>

      <p className="relative z-10 mt-10 text-xs text-[#B8AEA3] font-sans">
        v2.0 · Edusico Bordona
      </p>
    </div>
  );
};

function DecorativeGrid() {
  return (
    <div className="absolute inset-0 opacity-30 pointer-events-none">
      <svg className="w-full h-full" viewBox="0 0 1200 800" preserveAspectRatio="xMidYMid slice">
        <line x1="100" y1="400" x2="1100" y2="400" stroke="#D4CBC0" strokeWidth="1.5" />
      </svg>
    </div>
  );
}
